/**
 * quick-260513-bx4 — Project Detail "Engineering Activity" panel.
 * quick-260513-cc4 — 13-WEEK TREND block at top of panel body.
 *
 * Mounted on ProjectDetailPage directly after ProjectRepositoriesSection for
 * admins and PMs (same canManage gate). Hides itself entirely when the project
 * has 0 linked GitHub repos.
 *
 * Number/colour conventions mirror quick-260513-92r:
 *   - Intl.NumberFormat for comma-separated counts
 *   - Additions: text-green-700, prefix "+"
 *   - Deletions: text-red-700, prefix Unicode minus "−" (U+2212, NOT ASCII hyphen)
 *   - AI %: Math.round(aiPct * 100) + "%"
 */
import { useState, type ReactElement } from 'react';
import { Spinner } from '../common/Spinner';
import { useProjectRepositories } from '../../hooks/useProjectRepositories';
import { useProjectEngineeringStats } from '../../hooks/useProjectEngineeringStats';
import type { ProjectEngineeringStatsWindow } from '../../services/api/projectEngineeringStats';
import { EngineerSparkline } from './EngineerSparkline';

const numberFmt = new Intl.NumberFormat();
const WINDOW_OPTIONS: ProjectEngineeringStatsWindow[] = ['7d', '30d', '13w'];
const WINDOW_LABEL: Record<ProjectEngineeringStatsWindow, string> = {
  '7d': '7 days',
  '30d': '30 days',
  '13w': '13 weeks',
};

export interface ProjectEngineeringStatsSectionProps {
  projectId: number;
}

export function ProjectEngineeringStatsSection({
  projectId,
}: ProjectEngineeringStatsSectionProps): ReactElement | null {
  const [window, setWindow] = useState<ProjectEngineeringStatsWindow>('30d');
  const linkedQ = useProjectRepositories(projectId);
  const statsQ = useProjectEngineeringStats(projectId, window);

  // Hide section entirely when there are no linked repos. Guard against
  // first-render flash by waiting until the linked-repos query resolves.
  if (!linkedQ.isLoading && (linkedQ.data ?? []).length === 0) {
    return null;
  }

  const stats = statsQ.data;
  const hasCommits = !!stats && stats.totals.commits > 0;

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <div className="flex items-baseline justify-between">
        <h2 className="text-lg font-semibold text-gray-900">Engineering Activity</h2>
        <div className="flex items-center gap-2">
          {(statsQ.isLoading || linkedQ.isLoading) && <Spinner size="sm" />}
          <label className="sr-only" htmlFor="engineering-activity-window">
            Window
          </label>
          <select
            id="engineering-activity-window"
            className="rounded-md border-gray-300 text-sm shadow-sm focus:border-primary-500 focus:ring-primary-500"
            value={window}
            onChange={(e) => setWindow(e.target.value as ProjectEngineeringStatsWindow)}
          >
            {WINDOW_OPTIONS.map((w) => (
              <option key={w} value={w}>
                {WINDOW_LABEL[w]}
              </option>
            ))}
          </select>
        </div>
      </div>

      {stats && stats.sparkline.some((p) => p.value > 0) && (
        <div className="mt-4">
          <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
            13-week trend
          </div>
          <EngineerSparkline data={stats.sparkline} isSelf height={60} />
        </div>
      )}

      {statsQ.error && (
        <div className="mt-3 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
          Could not load engineering activity: {(statsQ.error as Error).message}
        </div>
      )}

      {!statsQ.isLoading && stats && !hasCommits && (
        <p className="mt-4 text-sm text-gray-500">No commits in the selected window.</p>
      )}

      {hasCommits && stats && (
        <>
          {/* KPI strip */}
          <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-gray-700">
            <span>
              <strong>{numberFmt.format(stats.totals.commits)}</strong> commits
            </span>
            <span>·</span>
            <span>
              <span className="text-green-700">+{numberFmt.format(stats.totals.additions)}</span>
              {' / '}
              <span className="text-red-700">{'−'}{numberFmt.format(stats.totals.deletions)}</span>
              {' lines'}
            </span>
            <span>·</span>
            <span>
              <strong>{numberFmt.format(stats.totals.filesChanged)}</strong> files
            </span>
            <span>·</span>
            <span>
              <strong>{Math.round(stats.totals.aiPct * 100)}%</strong> AI-assisted
            </span>
          </div>

          {/* Per-repo table */}
          <div className="mt-6">
            <h3 className="text-sm font-semibold text-gray-900 mb-2">By repository</h3>
            <table className="w-full table-fixed text-sm">
              <colgroup>
                <col style={{ width: '40%' }} />
                <col style={{ width: '15%' }} />
                <col style={{ width: '30%' }} />
                <col style={{ width: '15%' }} />
              </colgroup>
              <thead>
                <tr className="text-left text-gray-500 border-b border-gray-200">
                  <th className="py-1 pr-4 font-medium">Repo</th>
                  <th className="py-1 pr-4 font-medium text-right">Commits</th>
                  <th className="py-1 pr-4 font-medium text-right">+/&#x2212; lines</th>
                  <th className="py-1 pr-4 font-medium text-right">Files</th>
                </tr>
              </thead>
              <tbody>
                {stats.perRepo.map((r) => (
                  <tr key={r.repoId} className="border-b border-gray-100 last:border-0">
                    <td className="py-1 pr-4 font-mono text-gray-800">
                      {r.owner}/{r.name}
                    </td>
                    <td className="py-1 pr-4 text-right text-gray-900">
                      {numberFmt.format(r.commits)}
                    </td>
                    <td className="py-1 pr-4 text-right">
                      <span className="text-green-700">+{numberFmt.format(r.additions)}</span>
                      {' / '}
                      <span className="text-red-700">{'−'}{numberFmt.format(r.deletions)}</span>
                    </td>
                    <td className="py-1 pr-4 text-right text-gray-900">
                      {numberFmt.format(r.filesChanged)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Top-5 contributors */}
          {stats.topContributors.length > 0 && (
            <div className="mt-6">
              <h3 className="text-sm font-semibold text-gray-900 mb-2">Top contributors</h3>
              <table className="w-full table-fixed text-sm">
                <colgroup>
                  <col style={{ width: '40%' }} />
                  <col style={{ width: '15%' }} />
                  <col style={{ width: '30%' }} />
                  <col style={{ width: '15%' }} />
                </colgroup>
                <thead>
                  <tr className="text-left text-gray-500 border-b border-gray-200">
                    <th className="py-1 pr-4 font-medium">Name</th>
                    <th className="py-1 pr-4 font-medium text-right">Commits</th>
                    <th className="py-1 pr-4 font-medium text-right">+/&#x2212; lines</th>
                    <th className="py-1 pr-4 font-medium text-right">AI %</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.topContributors.map((c) => (
                    <tr key={c.userId} className="border-b border-gray-100 last:border-0">
                      <td className="py-1 pr-4 text-gray-800">{c.userName}</td>
                      <td className="py-1 pr-4 text-right text-gray-900">
                        {numberFmt.format(c.commits)}
                      </td>
                      <td className="py-1 pr-4 text-right">
                        <span className="text-green-700">+{numberFmt.format(c.additions)}</span>
                        {' / '}
                        <span className="text-red-700">{'−'}{numberFmt.format(c.deletions)}</span>
                      </td>
                      <td className="py-1 pr-4 text-right text-gray-900">
                        {Math.round(c.aiPct * 100)}%
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default ProjectEngineeringStatsSection;
